import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  IconButton, 
  AccountIcon, 
  SubscriptionIcon, 
  PreferencesIcon, 
  SecurityIcon, 
  NotificationsIcon, 
  DashboardIcon, 
  MessageIcon,
  ShareIcon, 
  FeedbackIcon, 
  LogoutIcon,
  XIcon 
} from "../../design-system";
import { surfaces, borders, textColors } from "../../design-system/tokens/colors";
import { AuthContext } from "../context/AuthContext";
import { APP_DOMAIN } from "../utils/config";

const UserProfileDropdown = ({ username, showDashboardLink = true, showSettings = true }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { auth, logout } = useContext(AuthContext);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const handleCopyToClipboard = async () => {
    const textToCopy = `Create your free professional website! Join ${APP_DOMAIN}`;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopyMessage("Link copied to clipboard!");
      setTimeout(() => setCopyMessage(""), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
      setCopyMessage("Failed to copy");
      setTimeout(() => setCopyMessage(""), 2000);
    }
  };

  const handleLogout = async () => {
    await logout();
    closeDropdown();
    navigate("/edit/login", { replace: true });
  };

  const settingsItems = [
    {
      name: "Account",
      path: `/settings/${username}/account`,
      icon: <AccountIcon size="sm" />
    },
    {
      name: "Subscription",
      path: `/settings/${username}/subscription`,
      icon: <SubscriptionIcon size="sm" />
    },
    {
      name: "Preferences",
      path: `/settings/${username}/preferences`,
      icon: <PreferencesIcon size="sm" />
    },
    {
      name: "Security",
      path: `/settings/${username}/security`,
      icon: <SecurityIcon size="sm" />
    },
    {
      name: "Notifications",
      path: `/settings/${username}/notifications`,
      icon: <NotificationsIcon size="sm" />
    }
  ];

  const profile = useMemo(() => {
    const resolvedUsername = auth?.username || username || "";
    return {
      firstName: auth?.firstName || "",
      lastName: auth?.lastName || "",
      email: auth?.email || "",
      username: resolvedUsername,
      subscriptionPlanLabel: auth?.subscriptionPlanLabel || null,
      isPaidSubscription: !!auth?.isPaidSubscription,
    };
  }, [auth, username]);

  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ");

  const badge = useMemo(() => {
    const label = String(profile.subscriptionPlanLabel || "").toUpperCase();
    if (label !== "PLUS" && label !== "PRO") return null;

    const isPro = label === "PRO";
    
    // Gold for PLUS, Emerald for PRO
    const bgClass = isPro 
      ? "bg-gradient-to-r from-emerald-400 to-teal-500 text-white border-teal-500 shadow-sm"
      : "bg-gradient-to-r from-[#E1BE46] to-[#BE962D] text-white border-[#BE962D] shadow-sm";

    return (
      <span
        className={[
          "absolute -bottom-1 -right-2",
          "px-1 py-0.5",
          "rounded-md",
          "text-[8px] leading-none font-bold tracking-widest",
          "border",
          "shadow-sm",
          bgClass,
        ].join(" ")}
        aria-label={`${label} subscription`}
      >
        {label}
      </span>
    );
  }, [profile.subscriptionPlanLabel]);

  return (
    <div className="relative" ref={dropdownRef}>
      <IconButton
        onClick={toggleDropdown}
        variant="primary"
        icon={
          <span className="relative inline-flex items-center justify-center w-full h-full">
            <span className="text-white font-medium text-sm">
              {(profile.username || "?")?.charAt(0).toUpperCase()}
            </span>
            {badge}
          </span>
        }
        title="User menu"
        aria-label="User menu"
      />

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div className={`absolute right-0 mt-2 w-64 ${surfaces.elevated} rounded-md shadow-lg border ${borders.default} z-50`}>
          {/* User Info Header */}
          <div className={`px-4 py-3 border-b ${borders.default}`}>
            <div className={`text-sm font-medium ${textColors.primary}`}>
                {profile.username}
            </div>
            {fullName && (
              <div className={`text-sm ${textColors.tertiary}`}>
                {fullName}
              </div>
            )}
            
            {/* Subscription Plan Info */}
            {(() => {
              const label = String(profile.subscriptionPlanLabel || "").toUpperCase();
              if (label !== "PLUS" && label !== "PRO") return null;
              
              const isPro = label === "PRO";
              const bgClass = isPro 
                ? "bg-gradient-to-r from-emerald-400 to-teal-500 text-white border-teal-500 shadow-sm"
                : "bg-gradient-to-r from-[#E1BE46] to-[#BE962D] text-white border-[#BE962D] shadow-sm";

              return (
                <div className="mt-2.5 flex flex-col items-start">
                  <span
                    className={[
                      "px-2 py-1",
                      "rounded-md",
                      "text-[10px] leading-none font-bold tracking-widest",
                      "border",
                      "shadow-sm",
                      bgClass,
                    ].join(" ")}
                  >
                    {label} PLAN
                  </span>
                  {!profile.isPaidSubscription && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 italic mt-1.5">
                      Gifted via Access Grant
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Navigation Links */}
          <div className="py-1">
            {/* Dashboard Link */}
            {showDashboardLink && (
              <Link
                to={`/edit/${username}`}
                onClick={closeDropdown}
                className={`flex items-center px-4 py-2 text-sm ${textColors.secondary} ${surfaces.hover} transition-colors`}
              >
                <DashboardIcon size="sm" className="mr-3" />
                Dashboard
              </Link>
            )}

            {/* Messages Link */}
            {showDashboardLink && (
              <Link
                to={`/dashboard/${username}/messages`}
                onClick={closeDropdown}
                className={`flex items-center px-4 py-2 text-sm ${textColors.secondary} ${surfaces.hover} transition-colors`}
              >
                <MessageIcon size="sm" className="mr-3" />
                Messages
              </Link>
            )}

            {/* Share with friends */}
            <button
              onClick={handleCopyToClipboard}
              className={`w-full flex items-center px-4 py-2 text-sm ${textColors.secondary} ${surfaces.hover} transition-colors`}
            >
              <ShareIcon size="sm" className="mr-3" />
              Share with friends!
            </button>

            {/* Give us your feedback */}
            <Link
              to="/feedback"
              onClick={closeDropdown}
              className={`flex items-center px-4 py-2 text-sm ${textColors.secondary} ${surfaces.hover} transition-colors`}
            >
              <FeedbackIcon size="sm" className="mr-3" />
              Give us your feedback
            </Link>

            {/* Copy feedback message */}
            {copyMessage && (
              <div className="px-4 py-2 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-l-2 border-green-500 dark:border-green-400">
                {copyMessage}
              </div>
            )}

            {/* Divider */}
            {showSettings && <div className={`border-t ${borders.default} my-1`}></div>}

            {/* Settings Links */}
            {showSettings && settingsItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={closeDropdown}
                className={`flex items-center px-4 py-2 text-sm ${textColors.secondary} ${surfaces.hover} transition-colors`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.name}
              </Link>
            ))}

            {/* Divider before logout */}
            <div className={`border-t ${borders.default} my-1`}></div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className={`w-full flex items-center px-4 py-2 text-sm ${textColors.secondary} ${surfaces.hover} transition-colors`}
            >
              <LogoutIcon size="sm" className="mr-3" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;
